import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  SectionHeading, EmptySection, Field, BoolBadge, TagList, formatTimestamp,
} from './shared';
import PhotoGrid from './PhotoGrid';
import type { PhotoAttachmentWithUrl } from '@/services/db/dsrDetail';

interface Props {
  data: any[];
  photos?: PhotoAttachmentWithUrl[];
}

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'bg-mozaic-teal/15 text-mozaic-teal',
  moderate: 'bg-mozaic-blue/15 text-mozaic-blue',
  serious: 'bg-mozaic-orange/15 text-mozaic-orange',
  critical: 'bg-mozaic-red/20 text-mozaic-red font-bold',
};

const WORK_STATUS_COLORS: Record<string, string> = {
  stopped: 'bg-mozaic-red/15 text-mozaic-red',
  resumed: 'bg-mozaic-green/15 text-mozaic-green',
  ongoing: 'bg-mozaic-orange/15 text-mozaic-orange',
};

export default function SWAEventsSection({ data, photos = [] }: Props) {
  return (
    <section>
      <SectionHeading id="section-swa-events" title="Stop Work Authority Events" count={data.length} />
      {data.length === 0 ? (
        <EmptySection name="SWA events" />
      ) : (
        <div className="mt-2 space-y-3">
          {data.map((row: any) => (
            <Card key={row.id}>
              <CardContent className="pt-4">
                <div className="mb-2 flex items-center gap-2">
                  <Badge className={SEVERITY_COLORS[row.severity] ?? 'bg-muted text-muted-foreground'}>
                    {row.severity}
                  </Badge>
                  <Badge className={WORK_STATUS_COLORS[row.work_status] ?? 'bg-muted text-muted-foreground'}>
                    {(row.work_status ?? '').replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Field label="Invoked At" value={formatTimestamp(row.invoked_at)} />
                  <Field label="Invoked By" value={row.invoked_by} />
                  <Field label="Site Location" value={row.site_location} />
                  <div className="col-span-full">
                    <Field label="Hazard Description" value={row.hazard_description} />
                  </div>
                  <div className="col-span-full">
                    <Field label="Action Taken" value={row.action_taken} />
                  </div>
                  <Field label="Activity" value={row.activity_description} />
                  <Field label="Work Crew" value={row.work_crew} />
                  <Field label="Personnel Count" value={row.personnel_count} />
                  <Field label="Supervisor Notified" value={<BoolBadge value={row.is_supervisor_notified} />} />
                  <Field label="Supervisor" value={row.supervisor_name} />
                  <Field label="Management Notified" value={<BoolBadge value={row.is_management_notified} />} />
                  <div className="col-span-full">
                    <Field label="Categories" value={<TagList tags={row.category} />} />
                  </div>
                  {row.outcome && (
                    <div className="col-span-full">
                      <Field label="Outcome" value={row.outcome} />
                    </div>
                  )}
                  <Field label="Authorized Resumption By" value={row.authorized_resumption_by} />
                  <Field label="Resumption Time" value={formatTimestamp(row.resumption_time)} />
                  {row.lessons_learned && (
                    <div className="col-span-full">
                      <Field label="Lessons Learned" value={row.lessons_learned} />
                    </div>
                  )}
                </div>
                <PhotoGrid photos={photos} entityId={row.id} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
